import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wallet, Users, Receipt, Calculator, ArrowRight, CheckCircle } from "lucide-react";
import { Button, Card, CardContent } from "@tabipla/ui";

// undraw illustrations with Waritto's emerald color (#10b981)
const ILLUSTRATIONS = {
  hero: "/illustrations/pizza-sharing.svg",
  groups: "/illustrations/friends.svg",
  record: "/illustrations/savings.svg",
  calculate: "/illustrations/personal-finance.svg",
};

export function LandingPage() {
  const { t } = useTranslation();

  const features = [
    {
      icon: Users,
      title: t("landing.feature1Title", "わりかんを作成"),
      description: t("landing.feature1Description", "友達や家族と一緒に管理"),
      image: ILLUSTRATIONS.groups,
    },
    {
      icon: Receipt,
      title: t("landing.feature2Title", "支払いを記録"),
      description: t("landing.feature2Description", "誰がいくら払ったかサクッと記録"),
      image: ILLUSTRATIONS.record,
    },
    {
      icon: Calculator,
      title: t("landing.feature3Title", "自動で精算"),
      description: t("landing.feature3Description", "誰に払えばいいか自動計算"),
      image: ILLUSTRATIONS.calculate,
    },
  ];

  const benefits = [
    t("landing.benefit1", "アカウント登録なしでも招待から参加可能"),
    t("landing.benefit2", "LINEでかんたんに招待"),
    t("landing.benefit3", "オフラインでも記録できる"),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-emerald-50/50 dark:from-gray-950 dark:via-gray-900 dark:to-emerald-950/30">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500 shadow-lg">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-emerald-600">Waritto</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                {t("auth.login")}
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-emerald-500 text-white hover:bg-emerald-600">
                {t("auth.register")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="container mx-auto max-w-5xl px-4">
        <section className="py-12 lg:py-20">
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
            {/* Hero Text */}
            <div className="max-w-xl text-center lg:text-left">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-6xl">
                <span className="text-emerald-600">{t("landing.heroHighlight", "割り勘")}</span>
                <span className="inline sm:hidden">
                  <br />
                </span>
                {t("landing.heroTitle", "をもっとシンプルに")}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground">
                {t(
                  "landing.description",
                  "食事や旅行の支払いをかんたんに記録・精算。もう「誰がいくら払った？」で悩む必要はありません。"
                )}
              </p>

              {/* Benefits */}
              <ul className="mt-8 space-y-3">
                {benefits.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 text-muted-foreground lg:justify-start"
                  >
                    <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row lg:justify-start">
                <Link to="/register">
                  <Button
                    size="lg"
                    className="w-full bg-emerald-500 text-white shadow-lg hover:bg-emerald-600 sm:w-auto"
                  >
                    {t("landing.getStarted", "無料で始める")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="hidden shrink-0 sm:block">
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-emerald-100/50 blur-2xl dark:bg-emerald-900/20" />
                <img
                  src={ILLUSTRATIONS.hero}
                  alt={t("landing.heroImageAlt", "友達と食事を分け合う")}
                  className="relative h-64 w-auto object-contain sm:h-72 lg:h-80"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t("landing.featuresTitle", "Waritto でできること")}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {t("landing.featuresSubtitle", "シンプルな3ステップで精算完了")}
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="border-0 bg-white/80 shadow-lg backdrop-blur dark:bg-gray-900/80"
              >
                <CardContent className="p-6">
                  <div className="flex justify-center">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="h-40 w-full object-contain"
                    />
                  </div>
                  <div className="mt-6 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
                      <feature.icon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold">{feature.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="py-16">
          <div className="rounded-3xl bg-emerald-600 p-8 text-white dark:bg-emerald-700 sm:p-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold">
                {t("landing.howItWorksTitle", "使い方はかんたん")}
              </h2>
              <p className="mt-4 text-emerald-100">
                {t("landing.howItWorksSubtitle", "3ステップで精算がスムーズに")}
              </p>
            </div>

            <div className="mt-12 grid gap-8 sm:grid-cols-3">
              {[
                {
                  step: "1",
                  title: t("landing.step1Title", "わりかんを作成"),
                  description: t("landing.step1Description", "友達を招待して始めよう"),
                },
                {
                  step: "2",
                  title: t("landing.step2Title", "支払いを記録"),
                  description: t("landing.step2Description", "誰が何にいくら払ったかを記録"),
                },
                {
                  step: "3",
                  title: t("landing.step3Title", "精算を確認"),
                  description: t("landing.step3Description", "誰が誰にいくら払うか自動計算"),
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-emerald-100">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t("landing.ctaTitle", "今すぐ始めよう")}
            </h2>
            <p className="mt-4 text-muted-foreground">
              {t("landing.ctaSubtitle", "登録かんたん、ずっと無料")}
            </p>
            <div className="mt-8">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-emerald-500 text-white shadow-lg hover:bg-emerald-600"
                >
                  {t("landing.getStarted", "無料で始める")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8">
        <div className="border-t pt-8">
          <p className="text-center text-sm text-muted-foreground">
            © {new Date().getFullYear()} Waritto. {t("landing.footer", "割り勘をシンプルに。")}
          </p>
        </div>
      </footer>
    </div>
  );
}
